// Pure helpers for wind-cycles-table — no React deps, easy to unit test.
import { fmt, timeAgo } from "@/lib/format";
import type { WindCycle } from "@/types/api";

export const TAKER_FEE_RATE = 0.0007;
export const MAKER_FEE_RATE = 0.0002;

export const REASON_TEXT: Record<string, string> = {
  tp: "Take profit — original tp_pct level hit",
  sl: "Stop loss — SL trigger fired",
  "be-maker": "Break-even maker — TP amended to BE-maker (age ≥ 2× hold)",
  "be-taker": "Break-even taker — TP amended to BE-taker (hold ≤ age < 2× hold)",
  force: "Force exit — force_minutes timeout reached",
  manual: "Manual close — user clicked Close Cycle / Stop Session",
  "ai-cut": "AI cut — advisor triggered early exit",
  "ai-skip": "AI skip — advisor declined to open this cycle (retry after cooldown)",
};

/** Whether a cycle still has an open position on the exchange. */
export function isCycleLive(c: WindCycle): boolean {
  return c.status === "open" || c.status === "closing" || c.status === "pending";
}

/** Orphan = closed but entry never filled properly. */
export function isCycleOrphan(c: WindCycle): boolean {
  return !isCycleLive(c) && (!c.entry_price || c.entry_price === 0 || !c.quantity);
}

/** Estimated unrealized PnL for a live cycle given current mark price. */
export function estimateCyclePnl(c: WindCycle, markPrice: number): number {
  if (!c.entry_price || !c.quantity) return 0;
  const dir = c.direction === "long" ? 1 : -1;
  const closeFee = markPrice * c.quantity * TAKER_FEE_RATE;
  return (
    (markPrice - c.entry_price) * c.quantity * dir -
    (c.entry_fee ?? 0) -
    closeFee
  );
}

/** Freshness window for AI-origin detection on cycle TP/SL/stage badges.
 *  Matches the runtime advisor's tp_tier 5min freshness plus a small margin
 *  so a recent tp_push / sl_trail still shows AI origin. Outside this
 *  window we treat the state as local (time-based / manual) even if AI
 *  originally set it, since the advice is stale. */
const AI_ORIGIN_FRESH_MS = 10 * 60_000;

export interface CycleAiOrigin {
  /** tp_tier override drove the current be-taker / be-maker stage. */
  tpTierAi: boolean;
  /** tp_push_bp was applied — current cycle TP is AI-pushed past session.tp_pct. */
  tpPushed: boolean;
  /** sl_trail_bp was applied — SL algo is AI-trailed to a tighter price. */
  slTrailed: boolean;
}

const EMPTY_AI_ORIGIN: CycleAiOrigin = { tpTierAi: false, tpPushed: false, slTrailed: false };

/** Inspect cycle.ai_advice for fresh fields that indicate AI-origin on the
 *  current TP/SL state. Used by badge renderers so the user can tell at a
 *  glance whether "BE-M" came from the time-based BE-glide or from AI. */
export function cycleAiOrigin(c: WindCycle): CycleAiOrigin {
  if (!c.ai_advice) return EMPTY_AI_ORIGIN;
  try {
    const a = JSON.parse(c.ai_advice) as {
      triggered_at?: string;
      at?: string;
      tp_tier?: string | null;
      tp_push_bp?: number | null;
      sl_trail_bp?: number | null;
    };
    // triggered_at (monitor advice) preferred; fall back to `at` (pre-cycle
    // advice doesn't have triggered_at, only `at`).
    const ts = a.triggered_at ? new Date(a.triggered_at).getTime()
             : a.at ? new Date(a.at).getTime() : 0;
    if (!ts || Date.now() - ts >= AI_ORIGIN_FRESH_MS) return EMPTY_AI_ORIGIN;
    return {
      tpTierAi: !!a.tp_tier && (c.stage === "be-taker-armed" || c.stage === "be-maker-armed"),
      tpPushed: typeof a.tp_push_bp === "number" && a.tp_push_bp > 0,
      slTrailed: typeof a.sl_trail_bp === "number" && a.sl_trail_bp > 0,
    };
  } catch {
    return EMPTY_AI_ORIGIN;
  }
}

/** BE price shown in cycles table, colored by stage. Title carries AI
 *  provenance when the stage came from an AI override so the hover tooltip
 *  explains WHY the BE was snapped early/late. */
export function cycleBeInfo(
  c: WindCycle
): { price: number; color: string; title: string } {
  const isLong = c.direction === "long";
  const entry = c.entry_price!;
  const fallback = isLong
    ? entry * (1 + 2 * MAKER_FEE_RATE)
    : entry * (1 - 2 * MAKER_FEE_RATE);
  const origin = cycleAiOrigin(c);
  const aiSuffix = origin.tpTierAi ? " · AI override" : "";

  if (c.stage === "be-maker-armed") {
    return {
      price: c.tp_price ?? fallback,
      color: origin.tpTierAi ? "#60a5fa" : "#3b82f6",
      title: `BE-maker (TP snapped to tightest)${aiSuffix}`,
    };
  }
  if (c.stage === "be-taker-armed") {
    return {
      price: c.tp_price ?? fallback,
      color: origin.tpTierAi ? "#c084fc" : "#a855f7",
      title: `BE-taker (TP relaxed)${aiSuffix}`,
    };
  }
  return {
    price: c.tp_price ?? fallback,
    color: origin.tpPushed ? "#f59e0b" : "#444",
    title: origin.tpPushed ? "TP pushed out by AI (momentum capture)" : "Theoretical break-even (maker rate)",
  };
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  return timeAgo(iso);
}

export { fmt };
